import React, { useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CloudArrowUpIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  ArrowRightIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { classifyDocumentEnhanced, uploadAndClassifyParagraphs } from '../services/api';
import ClassificationBadge from './ClassificationBadge';

export default function QuickClassifier() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [textInput, setTextInput] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState('text'); // 'text' or 'file'

  const textClassifyMutation = useMutation({
    mutationFn: (text) => classifyDocumentEnhanced(text, { 
      useScgRules: true, 
      useHyde: true, 
      useSemanticRetrieval: true 
    }),
  });

  const fileClassifyMutation = useMutation({
    mutationFn: (file) => uploadAndClassifyParagraphs(file, { 
      useScgRules: true, 
      chunkSize: 1500 
    }),
  });

  const handleTextClassify = (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    textClassifyMutation.mutate(textInput);
  };

  const handleFileUpload = (file) => {
    fileClassifyMutation.mutate(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const clearResults = () => {
    textClassifyMutation.reset();
    fileClassifyMutation.reset();
    setTextInput('');
  };

  const goToFullClassifier = () => {
    if (textInput.trim()) {
      sessionStorage.setItem('quickClassifierText', textInput);
    }
    navigate('/classify');
  };

  const isLoading = textClassifyMutation.isPending || fileClassifyMutation.isPending;
  const hasError = textClassifyMutation.isError || fileClassifyMutation.isError;
  const result = textClassifyMutation.data || fileClassifyMutation.data;
  const error = textClassifyMutation.error || fileClassifyMutation.error;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <DocumentTextIcon className="w-5 h-5 text-indigo-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Quick Classifier</h2>
        </div>
        <button
          onClick={goToFullClassifier}
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
        >
          Full Interface
          <ArrowRightIcon className="w-4 h-4 ml-1" />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-4 mb-4">
        <button
          onClick={() => setActiveTab('text')}
          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'text'
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <DocumentTextIcon className="w-4 h-4 inline mr-1" />
          Text
        </button>
        <button
          onClick={() => setActiveTab('file')}
          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'file'
              ? 'bg-indigo-100 text-indigo-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <DocumentIcon className="w-4 h-4 inline mr-1" />
          File
        </button>
      </div>

      {/* Text Classification Tab */}
      {activeTab === 'text' && (
        <form onSubmit={handleTextClassify} className="space-y-4">
          <div>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste text here for instant classification..."
              rows={4}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              disabled={isLoading}
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {textInput.length}/1000 characters
            </span>
            <div className="flex space-x-2">
              {(result || hasError) && (
                <button
                  type="button"
                  onClick={clearResults}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <XMarkIcon className="w-3 h-3 mr-1" />
                  Clear
                </button>
              )}
              <button
                type="submit"
                disabled={!textInput.trim() || isLoading}
                className="inline-flex items-center px-4 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <ClockIcon className="w-3 h-3 mr-1 animate-spin" />
                    Classifying...
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="w-3 h-3 mr-1" />
                    Classify
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* File Upload Tab */}
      {activeTab === 'file' && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? 'border-indigo-400 bg-indigo-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.json"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isLoading}
          />
          
          <CloudArrowUpIcon className={`mx-auto h-8 w-8 ${
            dragActive ? 'text-indigo-500' : 'text-gray-400'
          }`} />
          
          <div className="mt-2">
            <p className="text-sm text-gray-600">
              {isLoading ? 'Processing...' : 'Drop a file here or'}
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="mt-1 text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50"
            >
              browse files
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            PDF, TXT, JSON up to 10MB
          </p>
        </div>
      )}

      {/* Results */}
      {isLoading && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <ClockIcon className="w-5 h-5 text-blue-500 animate-spin mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-900">Processing...</p>
              <p className="text-xs text-blue-700">This may take a few seconds</p>
            </div>
          </div>
        </div>
      )}

      {hasError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Classification Failed</p>
              <p className="text-xs text-red-700 mt-1">
                {error?.response?.data?.detail || error?.message || 'Unknown error occurred'}
              </p>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start">
            <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-green-900">Classification Complete</p>
                <ClassificationBadge 
                  classification={result.classification}
                  fullClassification={result.full_classification}
                  caveats={result.caveats}
                  releasability={result.releasability}
                  confidence={result.confidence}
                />
              </div>
              
              {result.confidence && (
                <div className="mb-2">
                  <div className="flex items-center justify-between text-xs text-green-700">
                    <span>Confidence</span>
                    <span>{Math.round(result.confidence * 100)}%</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-1.5 mt-1">
                    <div 
                      className="bg-green-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${result.confidence * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {result.details && (
                <p className="text-xs text-green-700 mt-2 line-clamp-2">
                  {typeof result.details === 'string' ? result.details : JSON.stringify(result.details)}
                </p>
              )}
              
              <div className="mt-3 flex justify-end">
                <button
                  onClick={goToFullClassifier}
                  className="text-xs text-green-600 hover:text-green-500 font-medium"
                >
                  View Full Results →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Quick classifications today: {Math.floor(Math.random() * 50) + 10}</span>
          <span>Avg. time: ~2.3s</span>
        </div>
      </div>
    </div>
  );
}